import { Fragment, memo, useEffect, useMemo, useRef, useState } from 'react'
import dayjs from 'dayjs'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import PauseIcon from '@mui/icons-material/Pause'
import { alpha, useTheme } from '@mui/material/styles'
import api from 'services/api'
import { Stamp } from 'components/core/Clock'
import { AUDIO_DAYS } from '../StreamIngest/EngineHeard'
import { litSx } from './vocabulary'

// The whole span a moment was cut from (owner, 2026-11-04): every word the
// engine heard in it, to judge whether a card should carry more of the
// conversation. No speakers, only the words, broken into paragraphs where the
// talk pauses, each opening on when it was said. The moment's cut is marked
// in full ink on a band, its quote in the quote's green as the card's whole
// moment marks it; the rest is the context around them, in the secondary ink.
// The box opens scrolled to the moment.
//
// A click on any word plays the span's own minutes from there, on into the
// next, and the word being said is lit as on the card. The words outlive the
// span's row (they are read through the moment); the minutes play for the
// audio's 30 days and while the row lives. The server lets staff hear a span
// only through a moment they may see.
//
// The same read gives "From this span" its minutes: a row the selector found
// and did not cut plays its window from them, and a cut row plays its own cut,
// which outlives the audio.

const DAY_MS = 86_400_000
// A pause this long starts a new paragraph.
const PARAGRAPH_GAP_MS = 2500
const NO_WORDS = []

// The marks, the legend's and the words' alike: the moment on a strong band of
// the accent, its quote on a stronger green, both in full ink and a heavier
// weight, so the rest reads as the context around them (owner, 2026-11-04:
// the moment has to stand out). The band reaches a little past the words with
// a box-shadow, and the weight never changes while a span plays, so nothing
// moves or re-wraps.
const mark = (color) => ({
  color: 'text.primary',
  fontWeight: 600,
  borderRadius: '4px',
  backgroundColor: color,
  boxShadow: `0 0 0 2px ${color}`,
  boxDecorationBreak: 'clone',
  WebkitBoxDecorationBreak: 'clone',
})
const momentMark = (theme) => mark(alpha(theme.palette.primary.main, 0.3))
const quoteMark = (theme) => mark(alpha(theme.palette.success.main, 0.45))

// What the player calls the span while it plays (`MomentsBrowser`'s `current.id`).
export const spanKey = (moment) => `span:${moment.id}`

// A ledger row's name in the player, while its window plays from the span.
const rowTag = (row) => `${row.start_ms}-${row.end_ms}`

// The moment's span, read once something on the card needs it (`seen` only
// ever turns on): its words for the fold, its minutes for every play.
export const useMomentSpan = (moment, seen) => {
  const [span, setSpan] = useState(null)
  const [error, setError] = useState(null)
  const known = moment.span_start_ms != null
  useEffect(() => {
    if (!seen || !known) return undefined
    let live = true
    api.streamMetrics
      .getMomentSpan({ momentId: moment.id })
      .then((data) => live && setSpan(data))
      .catch((err) => live && setError(err.message))
    return () => {
      live = false
    }
  }, [seen, known, moment.id])
  // Only the minutes still inside the audio's 30 days play. The bucket counts
  // them from upload, a little after they were said, so this errs early.
  const audible = useMemo(
    () =>
      span && {
        ...span,
        segments: span.segments.filter(
          (s) => Date.now() - s.start_ms < AUDIO_DAYS * DAY_MS
        ),
      },
    [span]
  )
  return { known, span, audible, error }
}

// Why nothing plays, or until when it will.
const audioNote = (span, audible) => {
  if (!span.segments.length)
    return 'its minutes went with the span’s row (90 days)'
  if (!audible.segments.length) return `its audio is gone (${AUDIO_DAYS} days)`
  const first = Math.min(...audible.segments.map((s) => s.start_ms))
  return `plays until about ${dayjs(first + AUDIO_DAYS * DAY_MS).format(
    'MMM D'
  )}`
}

// The fold's summary: how much was said, how long, where the moment sits.
export const spanWordsLine = (moment, state) => {
  if (!state.known) return 'no span on the moment'
  if (state.error) return state.error
  if (!state.span) return 'every word around the moment'
  const { span, audible } = state
  const words = span.words || NO_WORDS
  const last = words.length ? words[words.length - 1][2] : null
  const end = span.span_end_ms ?? last
  const minutes = (ms) => Math.max(0, Math.round(ms / 60000))
  return [
    `${words.length.toLocaleString()} words`,
    end != null && `${minutes(end - span.span_start_ms)} min`,
    `the moment ${minutes(moment.range_start_ms - span.span_start_ms)} min in`,
    audioNote(span, audible),
  ]
    .filter(Boolean)
    .join(' · ')
}

// Each word's place against the card: in the quote, in the cut, or around it.
const kindOf = (moment) => {
  const origin = moment.range_start_ms
  const quote = moment.quote || {}
  const quoteFrom = quote.start != null ? origin + quote.start * 1000 : null
  const quoteTo = quote.end != null ? origin + quote.end * 1000 : null
  return (start) => {
    if (quoteFrom != null && start >= quoteFrom - 1 && start <= quoteTo)
      return 'q'
    if (start >= moment.range_start_ms && start < moment.range_end_ms)
      return 'm'
    return ''
  }
}

// Paragraphs at the pauses, each a run of words of one kind after another, so
// a band crosses the spaces inside it. `first` is the paragraph's first word's
// place in the span.
const paragraphsOf = (words, kind) => {
  const paragraphs = []
  words.forEach(([text, start, end], index) => {
    const before = words[index - 1]
    if (!before || start - (before[2] ?? before[1]) >= PARAGRAPH_GAP_MS) {
      paragraphs.push({ first: index, start, runs: [] })
    }
    const paragraph = paragraphs[paragraphs.length - 1]
    const word = { text, start, index }
    const kindHere = kind(start)
    const run = paragraph.runs[paragraph.runs.length - 1]
    if (run && run.kind === kindHere) run.words.push(word)
    else paragraph.runs.push({ kind: kindHere, words: [word] })
  })
  return paragraphs
}

// The last word whose start the playhead has passed, as the card lights it.
const litAt = (words, ms) => {
  let low = 0
  let high = words.length - 1
  let found = null
  while (low <= high) {
    const middle = (low + high) >> 1
    if (words[middle][1] <= ms + 20) {
      found = middle
      low = middle + 1
    } else high = middle - 1
  }
  return found
}

// One paragraph, rendered again only when the lit word enters or leaves it.
const Paragraph = memo(({ paragraph, lit, zone }) => (
  <Box component="p" sx={{ m: 0, mb: 1.25 }}>
    <Box component="span" className="when">
      <Stamp ms={paragraph.start} zone={zone} format="second" />
    </Box>
    {paragraph.runs.map((run, r) => (
      <Fragment key={run.words[0].index}>
        {r > 0 && ' '}
        <span className={run.kind}>
          {run.words.map((word, w) => (
            <Fragment key={word.index}>
              {w > 0 && ' '}
              <span
                data-at={word.start}
                className={word.index === lit ? 'w lit' : 'w'}
              >
                {word.text}
              </span>
            </Fragment>
          ))}
        </span>
      </Fragment>
    ))}
  </Box>
))

const WholeSpan = ({ moment, player, state }) => {
  const theme = useTheme()
  const box = useRef(null)
  const { span, audible } = state
  const words = span?.words || NO_WORDS
  const paragraphs = useMemo(
    () => paragraphsOf(words, kindOf(moment)),
    [words, moment]
  )
  const key = spanKey(moment)
  const here = player.current?.id === key
  const playing = here && !player.paused
  const canPlay = Boolean(audible?.segments.length)
  const lit = here
    ? litAt(words, player.current.segmentStart + player.time * 1000)
    : null

  // Open on the moment, a third of the way down the box.
  useEffect(() => {
    const el = box.current?.querySelector('.m, .q')
    if (el) box.current.scrollTop = el.offsetTop - box.current.clientHeight / 3
  }, [paragraphs])

  // Keep the word being said in view while it plays.
  useEffect(() => {
    const el = box.current?.querySelector('.lit')
    if (!el) return
    const top = box.current.scrollTop
    if (
      el.offsetTop < top ||
      el.offsetTop > top + box.current.clientHeight - 24
    ) {
      box.current.scrollTop = el.offsetTop - box.current.clientHeight / 3
    }
  }, [lit])

  if (!state.known) return null
  if (state.error) return <Alert severity="info">{state.error}</Alert>
  if (!span) return <CircularProgress size={18} />

  // A pause resumes where it stopped; a row's window that ran out plays on
  // from there without its end.
  const toggle = () => {
    if (playing) player.pause()
    else if (here && player.current.until == null) player.resume()
    else if (here)
      player.playSpan(
        moment,
        audible,
        player.current.segmentStart + player.time * 1000
      )
    else if (words.length) player.playSpan(moment, audible, words[0][1])
  }
  const seek = (event) => {
    const at = event.target.dataset?.at
    if (canPlay && at != null) player.playSpan(moment, audible, Number(at))
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Tooltip
          title={
            canPlay
              ? playing
                ? 'Pause the span'
                : 'Play the span from its start; click any word to hear it from there'
              : `Nothing to play: ${audioNote(span, audible)}`
          }
        >
          <span>
            <IconButton
              size="small"
              color="primary"
              disabled={!canPlay}
              onClick={toggle}
            >
              {player.loading === key ? (
                <CircularProgress size={18} />
              ) : playing ? (
                <PauseIcon />
              ) : (
                <PlayArrowIcon />
              )}
            </IconButton>
          </span>
        </Tooltip>
        <Typography variant="caption" color="text.secondary">
          <Box component="span" sx={{ px: 0.5, ...momentMark(theme) }}>
            the moment
          </Box>{' '}
          <Box component="span" sx={{ px: 0.5, ...quoteMark(theme) }}>
            its quote
          </Box>{' '}
          and the rest of the span around them
        </Typography>
      </Box>
      {player.error?.id === key && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {player.error.message}
        </Alert>
      )}
      {!words.length && (
        <Typography variant="body2" color="text.secondary">
          The engine heard no words in this span.
        </Typography>
      )}
      <Box
        ref={box}
        onClick={seek}
        sx={{
          position: 'relative',
          maxHeight: 420,
          overflowY: 'auto',
          pr: 1,
          typography: 'body2',
          lineHeight: 1.8,
          color: 'text.secondary',
          '& .when': {
            mr: 1,
            fontSize: 11,
            color: 'text.disabled',
          },
          '& .m': momentMark(theme),
          '& .q': quoteMark(theme),
          '& .w': canPlay
            ? {
                cursor: 'pointer',
                borderRadius: '4px',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.12),
                },
              }
            : {},
          '& .lit, & .lit:hover': litSx(theme),
        }}
      >
        {paragraphs.map((paragraph, p) => {
          const next = paragraphs[p + 1]
          const holds =
            lit != null && lit >= paragraph.first && (!next || lit < next.first)
          return (
            <Paragraph
              key={paragraph.first}
              paragraph={paragraph}
              lit={holds ? lit : null}
              zone={moment.timezone}
            />
          )
        })}
      </Box>
    </Box>
  )
}

// "From this span"'s plays: a cut row plays its own cut, which outlives the
// audio; any other row plays its window from the span's minutes while they
// last. A press on the row playing pauses it. `error` is what went wrong
// playing one of these rows (the card's own cut says so on the card).
export const useLedgerPlay = (moment, player, state, rows) => {
  const key = spanKey(moment)
  const rowPlay = (row) => {
    if (row.moment_id) {
      return {
        playable: true,
        playing: player.current?.id === row.moment_id && !player.paused,
        help: 'Play its cut',
      }
    }
    const playing =
      player.current?.id === key &&
      player.current.tag === rowTag(row) &&
      !player.paused
    if (!state.span)
      return { playable: false, help: state.error || 'Reading the span…' }
    if (!state.audible.segments.length)
      return {
        playable: false,
        help: `Its window cannot play: ${audioNote(state.span, state.audible)}`,
      }
    return { playable: true, playing, help: 'Play its window from the span' }
  }
  const onPlay = (row) => {
    if (rowPlay(row).playing) player.pause()
    else if (row.moment_id) player.playWhole({ id: row.moment_id })
    else
      player.playSpan(
        moment,
        state.audible,
        row.start_ms,
        row.end_ms,
        rowTag(row)
      )
  }
  const failed = player.error?.id
  const error =
    failed &&
    (failed === key ||
      (failed !== moment.id && rows.some((row) => row.moment_id === failed)))
      ? player.error.message
      : null
  return { rowPlay, onPlay, error }
}

export default WholeSpan
